import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Compass, Search, Wrench } from "lucide-react";
import { Feature, useSession, useZone } from "@/components/session-provider";
import { formatDate } from "@/lib/time";
import {
  CONTENT_TYPE_LABELS,
  getKnowledgeHome,
  searchArticles,
  type ArticleListItem,
  type KnowledgeHome,
} from "@/lib/knowledge";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/kb/")({ component: KnowledgeBase });

/** Matches the server, which refuses a shorter term with a message rather than dropping it. */
const MINIMUM_SEARCH = 3;

/**
 * The library.
 *
 * ⚠️ **This is `kb.index.tsx` and NOT `kb.tsx`, which is what it was.** A bare `kb.tsx`
 * beside `kb.manage.tsx` makes the authoring screen a CHILD of this one, so `/kb/manage` drew
 * the library again unless this file rendered an `<Outlet />` — and it does not, because it is
 * a page rather than a layout. Nothing errored and nothing was blank: “Write articles” changed
 * the URL and redrew this screen, which reads as a dead button, and the rollout panel deciding
 * who the knowledge base exists for was unreachable with nothing on screen saying so. The index
 * convention is what every other prefix here uses (`tickets`, `people`, `reports`), and
 * `route-nesting.test.ts` now holds the whole directory to it.
 *
 * ⚠️ **Nothing here decides who may read what.** The rollout decides whether this screen is
 * reachable at all, the query filter and `security.KnowledgeAccessPolicy` decide which articles
 * come back, and both run before a word is drawn. What this file does is arrange them.
 *
 * ⚠️ **A failure renders AS a failure, never as an empty library** (D-122). "There are no
 * articles yet" and "the request was refused" are different facts, and collapsing them tells
 * somebody their organisation has written nothing when the truth is that a call did not answer.
 */
function KnowledgeBase() {
  const { hasFeature, user } = useSession();
  const zone = useZone();

  const [home, setHome] = useState<KnowledgeHome | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // ⚠️ Submitted, never per keystroke — and NOT for D-78's reason, which is that every ticket
  // row returned files a PHI read. Nothing here does. It is submitted because the server
  // refuses a term under three characters, so typing "pr" on the way to "printer" would draw
  // an error the person did not ask for on every second keystroke.
  const [term, setTerm] = useState("");
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<ArticleListItem[] | null>(null);
  const [searchFailed, setSearchFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    void getKnowledgeHome()
      .then((data) => {
        if (live) setHome(data);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setFailed(
          error instanceof ApiError ? error.message : "The knowledge base could not be loaded.",
        );
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, []);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    const trimmed = term.trim();
    if (trimmed.length < MINIMUM_SEARCH) return;

    setQuery(trimmed);
    setSearchFailed(null);
    setResults(null);

    void searchArticles(trimmed)
      .then(setResults)
      .catch((error: unknown) => {
        setSearchFailed(
          error instanceof ApiError ? error.message : "That search could not be run.",
        );
      });
  };

  const clear = () => {
    setTerm("");
    setQuery(null);
    setResults(null);
    setSearchFailed(null);
  };

  const canAuthor = user?.canAuthorKnowledge ?? false;

  // The route exists whatever the flag says and the endpoints answer 404 — this is the
  // courtesy that stops somebody landing on a screen with nothing on it and no explanation.
  if (!hasFeature(Feature.KnowledgeBase)) {
    return (
      <Empty
        title="The knowledge base is switched off"
        detail="An administrator can switch it on in Settings."
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 p-4 sm:p-6">
      <header className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
              <BookOpen className="size-5 text-muted-foreground" aria-hidden />
              Knowledge base
            </h1>
            <p className="text-[13px] text-muted-foreground">
              Answers to the things people ask us most. If one of these solves it, you do not need
              to raise a request.
            </p>
          </div>

          {canAuthor ? (
            <Link
              to="/kb/manage"
              className="shrink-0 rounded-md border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted"
            >
              Write articles
            </Link>
          ) : null}
        </div>

        <form onSubmit={submit} className="flex gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search for a problem, a system, or an error message"
              aria-label="Search the knowledge base"
              // ⚠️ 16px on touch, or iOS zooms the whole page on focus. CI guards this.
              className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-base sm:text-[13px]"
            />
          </div>
          <button
            type="submit"
            disabled={term.trim().length < MINIMUM_SEARCH}
            className="rounded-md bg-primary px-4 py-2 text-[13px] font-medium text-primary-foreground disabled:opacity-50"
          >
            Search
          </button>
        </form>
      </header>

      {query !== null ? (
        <SearchResults
          query={query}
          results={results}
          failed={searchFailed}
          zone={zone}
          onClear={clear}
        />
      ) : loading ? (
        <p className="text-[13px] text-muted-foreground">Loading…</p>
      ) : failed !== null ? (
        <Empty title="The knowledge base could not be loaded" detail={failed} />
      ) : home === null || (home.collections.length === 0 && home.recentlyUpdated.length === 0) ? (
        <Empty
          title="The library is being built"
          detail={
            canAuthor
              ? "Nothing has been published yet. Write the first article from the authoring screen."
              : "Nothing has been published yet. Raise a request in the meantime and the service desk will help."
          }
        />
      ) : (
        <div className="space-y-8">
          {home.collections.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                Browse
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {home.collections.map((collection) => (
                  <Link
                    key={collection.id}
                    to="/kb/$collection"
                    params={{ collection: collection.slug }}
                    className="rounded-lg border border-border p-4 transition-colors hover:bg-muted/50"
                  >
                    <p className="flex items-center gap-2 text-[13px] font-semibold">
                      <Compass className="size-4 text-muted-foreground" aria-hidden />
                      {collection.name}
                    </p>
                    {collection.description ? (
                      <p className="mt-1 text-[12px] text-muted-foreground">
                        {collection.description}
                      </p>
                    ) : null}
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {/* ⚠️ Counted through the reader's own set, so a shelf never advertises
                          articles they cannot open. */}
                      {collection.articleCount === 1
                        ? "1 article"
                        : `${collection.articleCount} articles`}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          {home.recentlyUpdated.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                Recently updated
              </h2>
              <ArticleList items={home.recentlyUpdated} zone={zone} />
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SearchResults({
  query,
  results,
  failed,
  zone,
  onClear,
}: {
  query: string;
  results: ArticleListItem[] | null;
  failed: string | null;
  zone: string;
  onClear: () => void;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[13px] font-medium">
          {results === null && failed === null
            ? `Searching for “${query}”…`
            : failed !== null
              ? `Could not search for “${query}”`
              : results!.length === 1
                ? `1 article matches “${query}”`
                : `${results!.length} articles match “${query}”`}
        </h2>
        <button
          type="button"
          onClick={onClear}
          className="text-[12px] text-muted-foreground underline underline-offset-2"
        >
          Clear
        </button>
      </div>

      {failed !== null ? (
        // ⚠️ The server's own sentence, where it sent one. A refusal that names the minimum
        // length is the only line telling the reader what to do next; replacing it with
        // "something went wrong" throws that away (D-169).
        <Empty title="That search could not be run" detail={failed} />
      ) : results !== null && results.length === 0 ? (
        <Empty
          title="Nothing matches that yet"
          detail="Try a different word, or raise a request and the service desk will help. What people search for and do not find is how the library grows."
        />
      ) : results !== null ? (
        <ArticleList items={results} zone={zone} />
      ) : null}
    </section>
  );
}

function ArticleList({ items, zone }: { items: ArticleListItem[]; zone: string }) {
  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {items.map((item) => (
        <li key={`${item.collectionSlug}/${item.slug}`}>
          <Link
            to="/kb/$collection/$slug"
            params={{ collection: item.collectionSlug, slug: item.slug }}
            className="block p-4 transition-colors hover:bg-muted/50"
          >
            <p className="text-[13px] font-medium">{item.title}</p>
            {item.summary ? (
              <p className="mt-1 text-[12px] text-muted-foreground">{item.summary}</p>
            ) : null}
            <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              <span className="rounded border border-border px-1.5 py-0.5">
                {CONTENT_TYPE_LABELS[item.contentType]}
              </span>
              <span>{item.collectionName}</span>
              {item.appliesTo ? <span>· {item.appliesTo}</span> : null}
              <span>· Updated {formatDate(item.updatedAtUtc, zone)}</span>
            </p>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Empty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className={cn("rounded-lg border border-dashed border-border p-8 text-center")}>
      <Wrench className="mx-auto size-5 text-muted-foreground" aria-hidden />
      <p className="mt-2 text-[13px] font-medium">{title}</p>
      <p className="mt-1 text-[12px] text-muted-foreground">{detail}</p>
    </div>
  );
}
